import { Injectable } from '@angular/core';
import { SchoolAtlasService, fallbackIfDelayed } from '@ui/shared/util-api';
import { FilterDataStatus, FilterOption } from '@ui/shared/util-filter';
import { BehaviorSubject, Observable, Subject, catchError, map, of, switchMap } from 'rxjs';
import { Result, ToResult, toDataResult } from '@team23/ts-result';
import { SchoolMapFilterConfigService } from '../school-map-filter-config';

type FilterDataResult = Result<Array<FilterOption>, FilterDataStatus>;

const loadingStatus = ToResult.fromErrorKey(FilterDataStatus.Loading);

@Injectable({ providedIn: 'root' })
export class SchoolMapFilterDataService {
    public typesOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);
    public educationalBranchesOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);
    public careServicesOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);
    public measureTypesOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);

    private readonly _reloadTypes$ = new Subject<void>();
    private readonly _reloadEducationalBranches$ = new Subject<void>();
    private readonly _reloadCareServices$ = new Subject<void>();
    private readonly _reloadMeasureTypes$ = new Subject<void>();

    constructor(
        private readonly _schoolMapApi: SchoolAtlasService,
        filterConfigService: SchoolMapFilterConfigService,
    ) {
        this._reloadTypes$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        this._createTypesOptionsFromAPI$(),
                        loadingStatus,
                    ),
                ),
            )
            .subscribe(typesResult => {
                this.typesOptions$.next(typesResult);
                filterConfigService.updateTypeValues(typesResult.data ?? []);
            });

        this._reloadEducationalBranches$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        this._createEducationalBranchesOptionsFromAPI$(),
                        loadingStatus,
                    ),
                ),
            )
            .subscribe(educationalBranchesOptions => {
                this.educationalBranchesOptions$.next(educationalBranchesOptions);
            });

        this._reloadCareServices$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        this._createCareServicesOptionsFromAPI$(),
                        loadingStatus,
                    ),
                ),
            )
            .subscribe(careServicesOptions => {
                this.careServicesOptions$.next(careServicesOptions);
            });

        this._reloadMeasureTypes$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        this._createMeasureTypesOptionsFromAPI$(),
                        loadingStatus,
                    ),
                ),
            )
            .subscribe(measureTypesOptions => {
                this.measureTypesOptions$.next(measureTypesOptions);
            });

        this._reloadTypes$.next();
        this._reloadEducationalBranches$.next();
        this._reloadCareServices$.next();
        this._reloadMeasureTypes$.next();
    }

    public reloadErrored(): void {
        if (this.typesOptions$.value.error) {
            this._reloadTypes$.next();
        }

        if (this.educationalBranchesOptions$.value.error) {
            this._reloadEducationalBranches$.next();
        }

        if (this.careServicesOptions$.value.error) {
            this._reloadCareServices$.next();
        }

        if (this.measureTypesOptions$.value.error) {
            this._reloadMeasureTypes$.next();
        }
    }

    private _createTypesOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._schoolMapApi.getAllFacilityTypes({})
            .pipe(
                map(result =>
                    result.data
                        .map(type => ({ value: type.id, label: type.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }

    private _createEducationalBranchesOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._schoolMapApi.getAllEducationalBranches({})
            .pipe(
                map(result =>
                    result.data
                        .map(branch => ({ value: branch.id, label: branch.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }

    private _createCareServicesOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._schoolMapApi.getAllEducationalCareServices({})
            .pipe(
                map(result =>
                    result.data
                        .map(service => ({ value: service.id, label: service.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }

    private _createMeasureTypesOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._schoolMapApi.getAllMeasureTypes({})
            .pipe(
                map(result =>
                    result.data
                        .map(measureType => ({ value: measureType.id, label: measureType.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }
}
